/** Public registration contracts (spec §10). */
import { z } from 'zod';
import { applicantTypeSchema, registrationStatusSchema } from './enums.js';
import { abnSchema, auAddressSchema, emailSchema, phoneSchema, slugSchema } from './primitives.js';
export const publicRegistrationSchema = z.object({
    /** The chamber the applicant is registering with. One shared form, many chambers. */
    chamberId: slugSchema,
    companyName: z.string().trim().min(2).max(200),
    abn: abnSchema,
    applicantType: applicantTypeSchema,
    tradingName: z.string().trim().max(200).optional().nullable(),
    contactFirstName: z.string().trim().min(1).max(80),
    contactLastName: z.string().trim().min(1).max(80),
    /**
     * Email is the username. The legacy system had a separate chosen username, which is
     * one more thing to forget and one more collision to resolve for no benefit (§10.1).
     */
    contactEmail: emailSchema,
    contactPhone: phoneSchema,
    address: auAddressSchema,
    /**
     * The people authorised to sign this exporter's declarations, and a specimen of each
     * signature. At least one is required.
     *
     * Collected here rather than chased afterwards because the chamber needs it before it
     * can review the first declaration, and an applicant filling in a form is the cheapest
     * moment anyone will ever have to ask. The chamber verifies them on approval; the
     * validity window is stamped on at that point, from the chamber's own policy.
     */
    signatories: z
        .array(z.object({
        fullName: z.string().trim().min(1).max(120),
        position: z.string().trim().min(1).max(120),
        email: emailSchema.optional().nullable(),
        specimenFilename: z.string().trim().max(255),
        specimenBase64: z.string().min(1),
    }))
        .min(1, 'Name at least one person authorised to sign your export declarations')
        .max(10),
    /** Present only when CAPTCHA_PROVIDER is configured. */
    captchaToken: z.string().max(5000).optional().nullable(),
    acceptedTerms: z.literal(true, {
        errorMap: () => ({ message: 'You need to accept the terms to register' }),
    }),
});
export const registrationDecisionSchema = z.object({
    note: z.string().trim().max(2000).optional().nullable(),
});
export const rejectRegistrationSchema = z
    .object({
    rejectionReasonId: z.string().nullable(),
    freeText: z.string().trim().max(2000).optional().nullable(),
})
    .refine((v) => v.rejectionReasonId !== null || (v.freeText ?? '').trim().length > 0, {
    message: 'Choose a reason, or describe it under Other',
    path: ['rejectionReasonId'],
});
/** Shares the clarification master list with the document workflow (§13.2) — one list a
 *  chamber maintains once, used by both "ask the submitter for something" flows. */
export const requestMoreInformationSchema = z
    .object({
    itemIds: z.array(z.string()).default([]),
    freeText: z.string().trim().max(4000).optional().nullable(),
    respondByDays: z.number().int().min(1).max(90).default(14),
})
    .refine((v) => v.itemIds.length > 0 || (v.freeText ?? '').trim().length > 0, {
    message: 'Pick at least one item, or write what you need',
    path: ['itemIds'],
});
/** The applicant's reply, submitted through a secure expiring link — no account needed. */
export const registrationResponseSchema = z.object({
    token: z.string().min(10),
    message: z.string().trim().max(4000).optional().nullable(),
    attachments: z
        .array(z.object({
        filename: z.string().max(255),
        contentBase64: z.string(),
        contentType: z.string().max(120).default('application/pdf'),
    }))
        .max(10)
        .default([]),
});
export const registrationSummarySchema = z.object({
    id: z.string(),
    chamberId: z.string(),
    companyName: z.string(),
    abn: z.string(),
    applicantType: applicantTypeSchema,
    contactName: z.string(),
    contactEmail: z.string(),
    status: registrationStatusSchema,
    submittedAt: z.string(),
    lastActivityAt: z.string(),
});
//# sourceMappingURL=registration.js.map